import {isNil} from 'ramda';

import {parseDate, type Nullish} from 'shared/web';

const STORAGE_KEY = 'eag-grid-bulk-op-dismissed';

/** bulkOperationId → ISO timestamp of the dismissal. */
type DismissedOperations = Record<string, string>;

// Every grid shares the owner of the browser-wide persistence key.
let snapshot: DismissedOperations = {};
let storedValue: string | Nullish;
const listeners = new Set<() => void>();

function isDismissedOperations(value: unknown): value is DismissedOperations {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every((entry) => typeof entry === 'string')
  );
}

function getSnapshot(): DismissedOperations {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw !== storedValue) {
      storedValue = raw;
      snapshot = {};
      const parsed: unknown = isNil(raw) ? {} : JSON.parse(raw);
      snapshot = isDismissedOperations(parsed) ? parsed : {};
    }
  } catch {
    // Unavailable storage preserves memory; invalid JSON leaves the reset snapshot empty.
  }
  return snapshot;
}

function writeStorage(entries: DismissedOperations): void {
  try {
    const serialized = JSON.stringify(entries);
    localStorage.setItem(STORAGE_KEY, serialized);
    storedValue = serialized;
  } catch {
    // localStorage full or unavailable — dismissals then last for the session only.
  }
}

function handleStorage(event: StorageEvent) {
  if (event.key !== STORAGE_KEY && !isNil(event.key)) {
    return;
  }
  const previous = snapshot;
  if (getSnapshot() !== previous) {
    listeners.forEach((listener) => listener());
  }
}

/**
 * Which finished operations the user closed from the footer panel. View state only — every other
 * operation fact comes from the bulk-operation API. Persisted per browser in localStorage
 * without expiry, because history can return operations of any age.
 */
export function createBulkOperationDismissals() {
  return {
    subscribe: (listener: () => void) => {
      if (listeners.size === 0) {
        window.addEventListener('storage', handleStorage);
      }
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
          window.removeEventListener('storage', handleStorage);
        }
      };
    },
    getSnapshot,
    dismiss: (bulkOperationIds: string[]) => {
      getSnapshot();
      const added = bulkOperationIds.filter((id) => isNil(snapshot[id]));
      if (added.length === 0) {
        return;
      }
      const dismissedAt = parseDate(Date.now()).toISOString();
      snapshot = {
        ...snapshot,
        ...Object.fromEntries(added.map((id) => [id, dismissedAt])),
      };
      writeStorage(snapshot);
      listeners.forEach((listener) => listener());
    },
  };
}

export type BulkOperationDismissals = ReturnType<typeof createBulkOperationDismissals>;
